/**
 * 计算圆上一点的圆心角度
 * @param {Object} center - 圆心坐标 { x, y }
 * @param {Object} point - 圆上一点坐标 { x, y }
 * @returns {number} 返回角度（单位：度）
 */
export const calculateArcAngle = (center, point) => {
  // 计算点与圆心的向量
  const dx = point.x - center.x;
  const dy = point.y - center.y;

  // 计算弧度（使用 Math.atan2 来计算角度）
  let radian = Math.atan2(dy, dx);

  return radian;
};

//根据角度计算圆上一点
export const calculateArcPoint = (center, angle, radius) => {
  let x = center.x + Math.cos(angle) * radius;
  let y = center.y + Math.sin(angle) * radius;
  return { x, y, z: 0 };
};
// 将弧度转换为角度 (0-360范围)
export const toDegrees = (rad) => {
  let deg = ((rad * 180) / Math.PI) % 360;
  return deg < 0 ? deg + 360 : deg;
};

// 判断角度是否在圆弧范围内
export const isAngleInArcRange = (angle, startAngle, endAngle) => {
  // 将角度标准化到 0-360 范围内
  const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

  angle = normalizeAngle(angle);
  startAngle = normalizeAngle(startAngle);
  endAngle = normalizeAngle(endAngle);

  if (startAngle <= endAngle) {
    return angle >= startAngle && angle <= endAngle ? 1 : -1;
  } else {
    return angle >= startAngle || angle <= endAngle ? 1 : -1;
  }
};
// 计算两点相对于圆心的角度差
export const calculateArcPointsAngle = (ptCenter, point1, point2) => {
  // 获取两点相对于圆心的角度（弧度，范围为-π到π）
  const angle1 = calculateArcAngle(ptCenter, point1);
  const angle2 = calculateArcAngle(ptCenter, point2);

  // 计算角度差
  let angleDiff = angle2 - angle1;

  // 处理角度差，确保结果在0到2π之间
  if (angleDiff < 0) {
    // 如果差值为负，需要考虑是加2π还是取反加2π
    // 比较两种可能的路径，选择较小的那个
    const path1 = angleDiff + 2 * Math.PI; // 顺时针
    const path2 = 2 * Math.PI + angleDiff; // 逆时针

    angleDiff = Math.min(path1, path2);
  } else if (angleDiff > Math.PI) {
    // 如果差值大于π，说明实际上应该走另一个方向
    angleDiff = -(2 * Math.PI - angleDiff);
  }

  return angleDiff;
};

//通过圆弧上三点计算圆心
export const calculateArcCenterByThreePoint = (pt1, pt2, pt3) => {
  const { x: x1, y: y1 } = pt1;
  const { x: x2, y: y2 } = pt2;
  const { x: x3, y: y3 } = pt3;

  // 检查三点是否共线
  const AB = new THREE.Vector3().subVectors(pt2, pt1);
  const AC = new THREE.Vector3().subVectors(pt3, pt1);

  // 计算叉积
  const crossProduct = new THREE.Vector3().crossVectors(AB, AC);
  // 判断是否共线：叉积长度为零
  if (crossProduct.length() < 1e-10) {
    return null;
  }

  // 中点
  const mid1 = { x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
  const mid2 = { x: (x2 + x3) / 2, y: (y2 + y3) / 2 };

  // 斜率，处理水平线的情况
  const slope1 = (y2 - y1) / (x2 - x1 || 1e-10);
  const slope2 = (y3 - y2) / (x3 - x2 || 1e-10);

  // 垂直线的斜率
  const perpSlope1 = Math.abs(slope1) < 1e-10 ? Infinity : -1 / slope1;
  const perpSlope2 = Math.abs(slope2) < 1e-10 ? Infinity : -1 / slope2;

  // 垂直线方程
  let centerX, centerY;
  if (perpSlope1 === Infinity) {
    centerX = mid1.x;
    centerY = perpSlope2 * centerX + (mid2.y - perpSlope2 * mid2.x);
  } else if (perpSlope2 === Infinity) {
    centerX = mid2.x;
    centerY = perpSlope1 * centerX + (mid1.y - perpSlope1 * mid1.x);
  } else {
    const b1 = mid1.y - perpSlope1 * mid1.x;
    const b2 = mid2.y - perpSlope2 * mid2.x;
    centerX = (b2 - b1) / (perpSlope1 - perpSlope2 || 1e-10);
    centerY = perpSlope1 * centerX + b1;
  }

  return { x: centerX, y: centerY };
};

//计算一个点在直线的左上方还是右下方
export const calculatePointPosition = (pt1, pt2, pt3) => {
  const { x: x1, y: y1 } = pt1;
  const { x: x2, y: y2 } = pt2;
  const { x: x3, y: y3 } = pt3;

  // 使用向量叉积判断点的位置
  const crossProduct = (x2 - x1) * (y3 - y1) - (y2 - y1) * (x3 - x1);

  if (crossProduct > 1e-2) {
    return 1; // 点在直线的左上方
  } else if (crossProduct < -1e-10) {
    return -1; // 点在直线的右下方
  } else {
    return 0; // 点在直线上
  }
};

//计算两条平行直线之间的距离
export const calculateParallelLineDistance = (
  line1_pt1,
  line1_pt2,
  line2_pt1
) => {
  let distance = 0;
  const lineOrigin = new THREE.Line3(line1_pt1, line1_pt2);

  const pt1_vec = new THREE.Vector3(line2_pt1.x, line2_pt1.y, line2_pt1.z);
  let InsectPt = null;

  InsectPt = lineOrigin.closestPointToPoint(pt1_vec);
  if (InsectPt) {
    distance = pt1_vec.distanceTo(InsectPt);
    const perLine = new THREE.Line3(InsectPt, pt1_vec);
    const vecDir = new THREE.Vector3();
    perLine.delta(vecDir);
    //处理精度问题
    vecDir.x = vecDir.x.toFixed(1);
    vecDir.y = vecDir.y.toFixed(1);
    if (vecDir.x < 0) {
      distance = -distance;
    } else if (vecDir.y < 0) {
      distance = -distance;
    }
  }
  return distance;
};
// 标准化所有角度到 [0, 2π) 范围
export const normalizeAngle = (a) => {
  let normalized = a % (2 * Math.PI);
  return normalized < 0 ? normalized + 2 * Math.PI : normalized;
};

//判断三点是否共线
export const arePointsCollinear = (pt1, pt2, pt3) => {
  const { x: x1, y: y1 } = pt1;
  const { x: x2, y: y2 } = pt2;
  const { x: x3, y: y3 } = pt3;
  const AB = new THREE.Vector3().subVectors(pt2, pt1);
  const AC = new THREE.Vector3().subVectors(pt3, pt1);
  const crossProduct = new THREE.Vector3().crossVectors(AB, AC);
  if (crossProduct.length() < 1e-10) {
    return true;
  }
  return false;
};

/**
 * 判断点是否在同一平面上
 * @param {Array} points - 点数组
 * @returns {boolean} 是否在同一平面上
 */
export const isPointsCoplanar = (points) => {
  if (points.length < 3) return true; // 少于3个点一定共面

  // 取前三个点构建平面
  const p1 = points[0];
  const p2 = points[1];
  const p3 = points[2];

  // 计算平面法向量
  const v1 = new THREE.Vector3().subVectors(p2, p1);
  const v2 = new THREE.Vector3().subVectors(p3, p1);
  const normal = new THREE.Vector3().crossVectors(v1, v2).normalize();

  // 如果法向量长度为0，说明三点共线，需要重新选择三点
  if (normal.length() < 1e-6) {
    // 尝试使用其他点
    for (let i = 3; i < points.length; i++) {
      const p4 = points[i];
      const v3 = new THREE.Vector3().subVectors(p4, p1);
      const newNormal = new THREE.Vector3().crossVectors(v1, v3).normalize();
      if (newNormal.length() > 1e-6) {
        normal.copy(newNormal);
        break;
      }
    }
    // 如果所有点都共线，则返回true
    if (normal.length() < 1e-6) return true;
  }

  // 计算平面方程 ax + by + cz + d = 0 中的d
  const d = -normal.dot(p1);

  // 检查其他所有点到平面的距离
  const tolerance = 1e-6; // 容差值
  for (let i = 3; i < points.length; i++) {
    const distance = Math.abs(normal.dot(points[i]) + d);
    if (distance > tolerance) {
      return false;
    }
  }

  return true;
};

/**
 * 计算一个三维顶点到一个三维平面上的投影点
 * @param {Object} point - 要投影的点 { x, y, z }
 * @param {Array} planePoints - 平面上的顶点集合 [{ x, y, z }, ...]
 * @returns {Object} 返回投影点的坐标 { x, y, z }
 */
export const calculatePointProjectionOnPlane = (point, planePoints) => {
  if (planePoints.length < 3) {
    throw new Error("平面至少需要3个顶点");
  }

  // 使用前三个点计算平面法向量
  const p1 = new THREE.Vector3(
    planePoints[0].x,
    planePoints[0].y,
    planePoints[0].z
  );
  const p2 = new THREE.Vector3(
    planePoints[1].x,
    planePoints[1].y,
    planePoints[1].z
  );
  const p3 = new THREE.Vector3(
    planePoints[2].x,
    planePoints[2].y,
    planePoints[2].z
  );

  // 计算两个向量
  const v1 = new THREE.Vector3().subVectors(p2, p1);
  const v2 = new THREE.Vector3().subVectors(p3, p1);

  // 计算法向量（叉积）
  const normal = new THREE.Vector3().crossVectors(v1, v2).normalize();

  // 计算点到平面的向量
  const pointToPlane = new THREE.Vector3(
    point.x - p1.x,
    point.y - p1.y,
    point.z - p1.z
  );

  // 计算点到平面的距离
  const distance = pointToPlane.dot(normal);

  // 计算投影点
  const projection = new THREE.Vector3(
    point.x - distance * normal.x,
    point.y - distance * normal.y,
    point.z - distance * normal.z
  );

  return {
    x: projection.x,
    y: projection.y,
    z: projection.z,
  };
};

/**
 * 判断两条线段是否相交
 * @param {THREE.Vector3} line1Start - 第一条线段的起点
 * @param {THREE.Vector3} line1End - 第一条线段的终点
 * @param {THREE.Vector3} line2Start - 第二条线段的起点
 * @param {THREE.Vector3} line2End - 第二条线段的终点
 * @returns {boolean} 是否相交
 */
const doLinesIntersect = (line1Start, line1End, line2Start, line2End) => {
  const ccw = (A, B, C) => {
    return (C.y - A.y) * (B.x - A.x) > (B.y - A.y) * (C.x - A.x);
  };

  return (
    ccw(line1Start, line2Start, line2End) !==
      ccw(line1End, line2Start, line2End) &&
    ccw(line1Start, line1End, line2Start) !==
      ccw(line1Start, line1End, line2End)
  );
};

/**
 * 判断点是否在多边形内部
 * @param {THREE.Vector3} point - 待判断的点
 * @param {Array<THREE.Vector3>} polygon - 多边形顶点数组
 * @returns {boolean} 是否在多边形内部
 */
const isPointInPolygon = (point, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

/**
 * 判断两个闭合轮廓线是否相交或重叠
 * @param {Array<THREE.Vector3>} vertices1 - 第一个轮廓线的顶点集合
 * @param {Array<THREE.Vector3>} vertices2 - 第二个轮廓线的顶点集合
 * @returns {boolean} 如果轮廓线相交或重叠返回true，否则返回false
 */
export const checkContoursIntersection = (vertices1, vertices2) => {
  // 参数检查
  if (
    !vertices1 ||
    !vertices2 ||
    vertices1.length < 3 ||
    vertices2.length < 3
  ) {
    return false;
  }

  // 检查线段相交
  for (let i = 0; i < vertices1.length; i++) {
    const line1Start = vertices1[i];
    const line1End = vertices1[(i + 1) % vertices1.length];

    for (let j = 0; j < vertices2.length; j++) {
      const line2Start = vertices2[j];
      const line2End = vertices2[(j + 1) % vertices2.length];

      if (doLinesIntersect(line1Start, line1End, line2Start, line2End)) {
        return true;
      }
    }
  }

  // 检查重叠
  // 检查vertices1的所有点是否在vertices2内部
  let allPointsInPolygon2 = true;
  for (const point of vertices1) {
    if (!isPointInPolygon(point, vertices2)) {
      allPointsInPolygon2 = false;
      break;
    }
  }

  // 检查vertices2的所有点是否在vertices1内部
  let allPointsInPolygon1 = true;
  for (const point of vertices2) {
    if (!isPointInPolygon(point, vertices1)) {
      allPointsInPolygon1 = false;
      break;
    }
  }

  // 如果任一轮廓的所有点都在另一个轮廓内部，则认为是重叠
  return allPointsInPolygon1 || allPointsInPolygon2;
};

const EPSILON = 1e-3;

/**
 * 判断两条直线是否存在重叠或部分重叠
 * @param {THREE.Vector3} line1Start - 第一条直线的起点
 * @param {THREE.Vector3} line1End - 第一条直线的终点
 * @param {THREE.Vector3} line2Start - 第二条直线的起点
 * @param {THREE.Vector3} line2End - 第二条直线的终点
 * @returns {boolean} 如果存在重叠或部分重叠返回true，否则返回false
 */
export const isLinesOverlapping = (
  line1Start,
  line1End,
  line2Start,
  line2End
) => {
  // 1. 判断是否平行
  const dx1 = line1End.x - line1Start.x;
  const dy1 = line1End.y - line1Start.y;
  const dx2 = line2End.x - line2Start.x;
  const dy2 = line2End.y - line2Start.y;
  const cross = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(cross) > EPSILON) {
    return false; // 不平行
  }

  // 2. 判断是否近似共线
  if (Math.abs(dy1) < 1 && Math.abs(dy2) < 1) {
    // 水平线，判断y值差异
    if (Math.abs(line1Start.y - line2Start.y) > 2.5) {
      return false;
    }
  } else if (Math.abs(dx1) < 1 && Math.abs(dx2) < 1) {
    // 竖直线，判断x值差异
    if (Math.abs(line1Start.x - line2Start.x) > 2.5) {
      return false;
    }
  } else {
    // 斜线，使用原有公式
    const isCollinear =
      Math.abs(
        (line1Start.x - line2Start.x) * dy2 -
          (line1Start.y - line2Start.y) * dx2
      ) < 1;
    if (!isCollinear) {
      return false;
    }
  }

  // 3. 投影区间是否重叠
  if (Math.abs(dx1) >= Math.abs(dy1)) {
    // 主要沿x轴
    const l1min = Math.min(line1Start.x, line1End.x);
    const l1max = Math.max(line1Start.x, line1End.x);
    const l2min = Math.min(line2Start.x, line2End.x);
    const l2max = Math.max(line2Start.x, line2End.x);
    const overlap = Math.min(l1max, l2max) - Math.max(l1min, l2min);
    return overlap > EPSILON;
  } else {
    // 主要沿y轴
    const l1min = Math.min(line1Start.y, line1End.y);
    const l1max = Math.max(line1Start.y, line1End.y);
    const l2min = Math.min(line2Start.y, line2End.y);
    const l2max = Math.max(line2Start.y, line2End.y);
    const overlap = Math.min(l1max, l2max) - Math.max(l1min, l2min);
    return overlap > EPSILON;
  }
};

const ARC_CENTER_TOLERANCE = 1e-6; // 圆心容差
const ARC_RADIUS_TOLERANCE = 1e-6; // 半径容差
const ARC_ANGLE_TOLERANCE = 1e-8; // 角度容差（弧度）

/**
 * 判断两段圆弧是否重叠或部分重叠
 * @param {Object} arc1Center - 第一段圆弧圆心 {x, y}
 * @param {Object} arc1Start - 第一段圆弧起点 {x, y}
 * @param {Object} arc1End - 第一段圆弧终点 {x, y}
 * @param {Object} arc2Center - 第二段圆弧圆心 {x, y}
 * @param {Object} arc2Start - 第二段圆弧起点 {x, y}
 * @param {Object} arc2End - 第二段圆弧终点 {x, y}
 * @returns {boolean} 是否重叠或部分重叠
 */
export function isArcsOverlapping(
  arc1Center,
  arc1Start,
  arc1End,
  arc2Center,
  arc2Start,
  arc2End
) {
  // 1. 判断圆心是否一致
  const centerDist = Math.hypot(
    arc1Center.x - arc2Center.x,
    arc1Center.y - arc2Center.y
  );
  if (centerDist > ARC_CENTER_TOLERANCE) return false;

  // 2. 判断半径是否一致
  const arc1Radius = Math.hypot(
    arc1Start.x - arc1Center.x,
    arc1Start.y - arc1Center.y
  );
  const arc2Radius = Math.hypot(
    arc2Start.x - arc2Center.x,
    arc2Start.y - arc2Center.y
  );
  if (Math.abs(arc1Radius - arc2Radius) > ARC_RADIUS_TOLERANCE) return false;

  // 3. 计算起点和终点的角度（以圆心为原点，atan2）
  const angleOf = (center, pt) => Math.atan2(pt.y - center.y, pt.x - center.x);
  let a1s = angleOf(arc1Center, arc1Start);
  let a1e = angleOf(arc1Center, arc1End);
  let a2s = angleOf(arc2Center, arc2Start);
  let a2e = angleOf(arc2Center, arc2End);

  // 4. 归一化角度到[0, 2π)
  const norm = (a) => ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  a1s = norm(a1s);
  a1e = norm(a1e);
  a2s = norm(a2s);
  a2e = norm(a2e);

  // 5. 处理跨0°的情况，把区间变成 [start, start+delta]
  const arcRange = (s, e) => {
    let delta = e - s;
    if (delta < 0) delta += 2 * Math.PI;
    return [s, s + delta];
  };
  let [r1s, r1e] = arcRange(a1s, a1e);
  let [r2s, r2e] = arcRange(a2s, a2e);

  // 6. 判断区间是否有重叠
  // 只要有交集就算重叠
  const overlap = Math.min(r1e, r2e) - Math.max(r1s, r2s);
  return overlap > ARC_ANGLE_TOLERANCE;
}
